// 거래처 「노선·출발시간표」(2026-10-13 게시판 YCAmtzH2 · CS 강유정) — import 0 순수 함수.
//
// 발단: 분실물 접수에서 고객이 노선명·출발시간을 직접 적어 거래처 실제 노선명과 안 맞았고, 기사님이
//       «어느 차였나»를 확인하기 어려웠다 → 거래처에 등록된 노선·출발시간 목록에서 **고르게** 한다.
//
// 🔴 저장값 = 표시값: times 는 상담원이 적은 글자 그대로(예 '출근 07:00', '퇴근 17:45') 저장되고,
//    위젯 선택지·티켓 `departureTime`·인박스·챗 카드·포털이 **매핑 없이** 같은 글자를 쓴다
//    (코드↔라벨 표를 두면 한쪽만 고쳐져 같은 티켓이 화면마다 다르게 보인다 — LOST_TIME_SLOTS 와 같은 원칙).
// 🔴 시간표가 없는 거래처는 위젯이 **지금과 완전히 같다**(새 게이트의 기본값은 현상 유지).
// `scripts/verify-route-schedule.cjs` 가 이 원문을 컴파일해 케이스·뮤테이션으로 잰다.

/** 노선 하나와 그 노선의 출발 시간 목록(표시값 그대로). times 가 비면 «시간 없는 노선». */
export interface RouteScheduleEntry {
  route: string;
  times: string[];
}

/** 출발 시간 한 칸의 최대 길이 — 서버(`ticketCore`)의 departureTime 상한과 같은 자. */
export const DEPARTURE_TIME_MAX = 20;

export interface RouteScheduleParseError {
  /** 1부터 세는 입력 줄 번호. */
  line: number;
  text: string;
  reason: string;
}

export interface RouteScheduleParseResult {
  schedule: RouteScheduleEntry[];
  errors: RouteScheduleParseError[];
}

/**
 * 출발 시간 한 칸 정규화 — '출근 7:00' → '출근 07:00' · '7:5' 는 잘못(분은 두 자리) · '07:00' 그대로.
 *   형식 = [이름표(선택)] + H:mm 또는 HH:mm. 잘못이면 null(조용히 버리지 않고 호출부가 오류로 알린다).
 */
export function normalizeDepartureToken(raw: string): string | null {
  const s = (raw ?? '').normalize('NFC').replace(/\s+/g, ' ').trim();
  if (!s) return null;
  const m = /^(.*?)\s*(\d{1,2})\s*:\s*(\d{2})$/.exec(s);
  if (!m) return null;
  const h = Number(m[2]);
  const mi = Number(m[3]);
  if (h > 23 || mi > 59) return null;
  const label = m[1]!.trim();
  const hhmm = `${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}`;
  const out = label ? `${label} ${hhmm}` : hhmm;
  return out.length > DEPARTURE_TIME_MAX ? null : out;
}

/** 출발 시간 표시값에서 'HH:mm'(없으면 null) — 기사 추정·같은 시각 판정용. */
export function departureHHmm(value: string | undefined | null): string | null {
  const m = /(\d{1,2}):(\d{2})\s*$/.exec((value ?? '').trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
}

/**
 * 콘솔 입력칸 → 시간표. 한 줄 = 한 노선, `노선명 = 출근 07:00, 퇴근 18:00`(`=` 없으면 시간 없는 노선).
 *   · 빈 줄은 건너뛴다. 같은 노선이 두 줄이면 시간을 합친다. 한 노선 안 같은 시간은 한 번만.
 *   · 🔴 잘못된 시간·빈 노선명은 **오류로 돌려준다**(몇 행인지) — 조용히 버리면 상담원은 적었는데
 *     고객 선택지엔 없는 시간이 생긴다. 호출부는 오류가 있으면 저장을 막는다.
 */
export function parseRouteSchedule(text: string): RouteScheduleParseResult {
  const schedule: RouteScheduleEntry[] = [];
  const errors: RouteScheduleParseError[] = [];
  const lines = (text ?? '').replace(/\r\n?/g, '\n').split('\n');
  lines.forEach((rawLine, i) => {
    const line = rawLine.normalize('NFC').trim();
    if (!line) return;
    const eq = line.indexOf('=');
    const route = (eq >= 0 ? line.slice(0, eq) : line).replace(/\s+/g, ' ').trim();
    if (!route) {
      errors.push({ line: i + 1, text: line, reason: '노선명이 비어 있습니다' });
      return;
    }
    const times: string[] = [];
    if (eq >= 0) {
      const tokens = line
        .slice(eq + 1)
        .split(/[,，]/)
        .map((x) => x.trim())
        .filter(Boolean);
      for (const tok of tokens) {
        const norm = normalizeDepartureToken(tok);
        if (!norm) {
          errors.push({ line: i + 1, text: line, reason: `시간 형식이 잘못됐습니다: '${tok}' (예: 출근 07:00)` });
          continue;
        }
        if (!times.includes(norm)) times.push(norm);
      }
    }
    const same = schedule.find((e) => e.route === route);
    if (same) {
      for (const t of times) if (!same.times.includes(t)) same.times.push(t);
    } else {
      schedule.push({ route, times });
    }
  });
  return { schedule, errors };
}

/**
 * 시간표(+옛 routes) → 입력칸 글. 🔴 `routes` 에만 있는 노선(시간표 도입 전 데이터·노선 FAQ 업로드)은
 *   **시간 없는 줄로 살린다** — 편집을 열었다 저장했을 뿐인데 노선이 사라지면 안 된다.
 */
export function routeScheduleToText(
  schedule: RouteScheduleEntry[] | undefined | null,
  routes?: string[] | null,
): string {
  const rows = cleanRouteSchedule(schedule);
  const names = new Set(rows.map((r) => r.route));
  for (const r of routes ?? []) {
    const name = (r ?? '').trim();
    if (name && !names.has(name)) {
      rows.push({ route: name, times: [] });
      names.add(name);
    }
  }
  return rows.map((r) => (r.times.length ? `${r.route} = ${r.times.join(', ')}` : r.route)).join('\n');
}

/** 저장된 값을 믿지 않고 모양만 걸러 쓴다(노선명 없는 행 제거·times 문자열만). */
export function cleanRouteSchedule(schedule: RouteScheduleEntry[] | undefined | null): RouteScheduleEntry[] {
  if (!Array.isArray(schedule)) return [];
  const out: RouteScheduleEntry[] = [];
  for (const e of schedule) {
    const route = typeof e?.route === 'string' ? e.route.trim() : '';
    if (!route || out.some((o) => o.route === route)) continue;
    const times = Array.isArray(e.times)
      ? e.times.filter((t): t is string => typeof t === 'string' && t.trim().length > 0).map((t) => t.trim())
      : [];
    out.push({ route, times: Array.from(new Set(times)) });
  }
  return out;
}

/**
 * 기사 명부(callcenter 미러 `cc_drivers`)의 노선·출근 시각을 시간표에 **합친다** — 저장은 사람이 한다.
 *   · 이미 있는 노선은 시각만 더한다(같은 HH:mm 이 이미 있으면 건너뛴다 — '07:00' 과 '출근 07:00' 중복 방지).
 *   · 명부의 시각은 **출근 하나뿐**이라 `출근 HH:mm` 으로 넣는다(퇴근은 상담원이 적어야 한다).
 *   · 잘못된 시각·빈 노선명 행은 건너뛴다(명부 쪽 데이터라 상담원이 고칠 수 없다).
 */
export function mergeDriverDepartures(
  schedule: RouteScheduleEntry[],
  drivers: { routeName?: string; departureTime?: string }[],
): { schedule: RouteScheduleEntry[]; added: number } {
  const out = schedule.map((e) => ({ route: e.route, times: [...e.times] }));
  let added = 0;
  for (const d of drivers) {
    const route = (d.routeName ?? '').normalize('NFC').replace(/\s+/g, ' ').trim();
    const hhmm = departureHHmm(normalizeDepartureToken(d.departureTime ?? ''));
    if (!route || !hhmm) continue;
    let entry = out.find((e) => e.route === route);
    if (!entry) {
      entry = { route, times: [] };
      out.push(entry);
    }
    if (entry.times.some((t) => departureHHmm(t) === hhmm)) continue;
    entry.times.push(`출근 ${hhmm}`);
    added += 1;
  }
  return { schedule: out, added };
}

// ── 자동 시간표(기사 명부 → tenants.routeScheduleAuto · 2026-10-14 게시판 MxnsrbK8 · CS 강유정) ──
//   발단: 손입력 시간표(routeSchedule)는 prod 0곳이었다. Buslink Ops 기사 마스터엔 거래처별 노선·출근 시각이
//   이미 있으므로 `syncCcDriversDaily`(06:20)가 미러 직후 거래처마다 계산해 `routeScheduleAuto` 에 둔다.
//   🔴 tenants 는 위젯 공개 read — 노선·`출근 HH:mm` 만. 기사 이름·전화·차량은 절대 넣지 않는다.
//   🔴 functions 는 shared 를 못 읽어 `functions/src/routeScheduleAuto.ts` 가 아래 autoRouteSchedule·
//      sortRouteSchedule·sameRouteSchedule(+파서 둘)을 **미러**한다 — 함께 고칠 것
//      (`scripts/verify-route-schedule.cjs` 가 같은 입력으로 두 원문을 대조한다).

/** 시간표의 출처 — manual = 상담원 손입력 · auto = 기사 명부 자동 · none = 없음(legacy 폼). */
export type RouteScheduleSource = 'manual' | 'auto' | 'none';

/** 출발 시간 select 의 «퇴근·그 밖의 시간» 값(auto 전용 탈출구). 저장되지 않는다 — 티켓엔 lostTime 만 간다. */
export const DEPARTURE_OTHER = '__other__';
export const DEPARTURE_OTHER_LABEL = '퇴근·그 밖의 시간';

/** 결정적 비교 — 🔴 localeCompare 금지(런타임 ICU 차이로 순서가 흔들리면 매일 «바뀌었다»로 write 가 난다). */
function codeCmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** 결정적 정렬 — 노선명(코드포인트) · 노선 안 시각(HH:mm, 시각 없으면 맨 뒤 → 글자). */
export function sortRouteSchedule(schedule: RouteScheduleEntry[]): RouteScheduleEntry[] {
  const key = (t: string) => departureHHmm(t) ?? '99:99';
  return schedule
    .map((e) => ({ route: e.route, times: [...e.times].sort((a, b) => codeCmp(key(a), key(b)) || codeCmp(a, b)) }))
    .sort((a, b) => codeCmp(a.route, b.route));
}

/** 기사 명부 → 자동 시간표(= `mergeDriverDepartures([], drivers)` + 결정적 정렬). */
export function autoRouteSchedule(drivers: { routeName?: string; departureTime?: string }[]): RouteScheduleEntry[] {
  return sortRouteSchedule(mergeDriverDepartures([], drivers).schedule);
}

/** 두 시간표가 같은가(순서까지) — 동기화가 «값이 바뀐 거래처만» 쓰는 판정. */
export function sameRouteSchedule(
  a: RouteScheduleEntry[] | undefined | null,
  b: RouteScheduleEntry[] | undefined | null,
): boolean {
  const x = Array.isArray(a) ? a : [];
  const y = Array.isArray(b) ? b : [];
  if (x.length !== y.length) return false;
  return x.every((e, i) => {
    const f = y[i];
    if (!e || !f || e.route !== f.route) return false;
    if (!Array.isArray(e.times) || !Array.isArray(f.times) || e.times.length !== f.times.length) return false;
    return e.times.every((t, j) => t === f.times[j]);
  });
}

/**
 * 위젯·콘솔이 쓰는 «유효 시간표» 단일 판정.
 *   · 손입력 routeSchedule 에 시간이 **하나라도** 있으면 그것(상담원이 손댄 거래처는 상담원 것이 이긴다).
 *   · 아니면 routeScheduleAuto(기사 명부 자동).
 *   · 둘 다 없으면 none → 빈 표(위젯 = 예전 폼 그대로).
 */
export function effectiveRouteSchedule(
  t: { routeSchedule?: RouteScheduleEntry[] | null; routeScheduleAuto?: RouteScheduleEntry[] | null } | null | undefined,
): { schedule: RouteScheduleEntry[]; source: RouteScheduleSource } {
  const manual = cleanRouteSchedule(t?.routeSchedule);
  if (manual.some((r) => r.times.length > 0)) return { schedule: manual, source: 'manual' };
  const auto = cleanRouteSchedule(t?.routeScheduleAuto);
  if (auto.some((r) => r.times.length > 0)) return { schedule: auto, source: 'auto' };
  return { schedule: [], source: 'none' };
}

/**
 * 위젯 분실물 폼의 노선·시간 칸 판정.
 *   · mode 'legacy' = 시간표에 노선이 없다 → **지금과 똑같은 폼**(등록 노선 드롭다운+기타 / 자유 입력 + 분실 시간대).
 *   · mode 'schedule' = 시간표 노선 select 필수 · 「기타 (직접 입력)」 없음.
 *       - 고른 노선이 목록에 없으면(문의 폼에서 넘어온 기타 입력 등) 아직 안 고른 것으로 본다.
 *       - 고른 노선에 시간이 있으면 「출발 시간」 select 필수 + 「분실 시간대」 숨김.
 *       - 고른 노선에 시간이 없으면 「분실 시간대」(LOST_TIME_SLOTS)로 폴백.
 *       - 노선을 아직 안 골랐으면 출발 시간 칸만 «노선을 먼저» 상태로 보인다.
 *   · source 'auto'(기사 명부 자동 시간표 · 2026-10-14)면 출발 시간에 「퇴근·그 밖의 시간」 탈출구가 붙고,
 *     그걸 고르면(departure === DEPARTURE_OTHER) 출발 시간 비필수 + 분실 시간대 필수. manual 은 지금과 같다.
 */
export interface LostRoutePlan {
  mode: 'legacy' | 'schedule';
  /** schedule 모드의 노선 선택지(시간표 순서). */
  routeOptions: string[];
  /** 목록 기준으로 «선택된» 노선(목록에 없으면 ''). */
  selectedRoute: string;
  /** 고른 노선의 출발 시간 선택지. */
  times: string[];
  /** 「출발 시간」 칸을 띄우는가. */
  showDeparture: boolean;
  /** 「출발 시간」 이 필수인가(= 고른 노선에 시간이 있다). */
  departureRequired: boolean;
  /** 「분실 시간대」 칸을 띄우는가(띄우면 필수). */
  showLostTimeSlot: boolean;
  /**
   * 출발 시간 select 맨 끝에 「퇴근·그 밖의 시간」(DEPARTURE_OTHER)을 둘지 — **자동 시간표(auto)일 때만**.
   *   🔴 기사 명부엔 출근 시각만 있어 퇴근길 분실 고객(실측 절반 가까이)이 시간을 못 고른다.
   *   손입력(manual)은 상담원이 퇴근까지 적은 표라 요청자 뜻대로 «목록에서만 필수» 그대로.
   */
  offerOther: boolean;
  /** 고객이 「퇴근·그 밖의 시간」을 골랐는가 → 출발 시간 비필수·미저장 + 분실 시간대 필수. */
  otherPicked: boolean;
}

export function lostRoutePlan(
  schedule: RouteScheduleEntry[] | undefined | null,
  route: string,
  source: RouteScheduleSource = 'manual',
  departure = '',
): LostRoutePlan {
  const rows = cleanRouteSchedule(schedule);
  if (rows.length === 0) {
    return {
      mode: 'legacy',
      routeOptions: [],
      selectedRoute: '',
      times: [],
      showDeparture: false,
      departureRequired: false,
      showLostTimeSlot: true,
      offerOther: false,
      otherPicked: false,
    };
  }
  const hit = rows.find((r) => r.route === route);
  const times = hit ? hit.times : [];
  // 퇴근길 탈출구 — auto 이고 고른 노선에 시각이 있을 때만(시각 없는 노선은 이미 분실 시간대로 폴백).
  const offerOther = source === 'auto' && !!hit && times.length > 0;
  const otherPicked = offerOther && departure === DEPARTURE_OTHER;
  return {
    mode: 'schedule',
    routeOptions: rows.map((r) => r.route),
    selectedRoute: hit ? hit.route : '',
    times,
    showDeparture: !hit || times.length > 0,
    departureRequired: !!hit && times.length > 0 && !otherPicked,
    showLostTimeSlot: !!hit && (times.length === 0 || otherPicked),
    offerOther,
    otherPicked,
  };
}

/**
 * 분실일시 한 줄 — `일자 + (출발 시간 있으면 그것 / 없으면 분실 시간대)`.
 *   '07:00' → '출발 07:00' · '퇴근 17:45' → '퇴근 17:45 출발'. 인박스·챗 카드·포털·전달 양식이 같은 규칙.
 *   🔴 functions(salesChat·companyBroadcast·clientPortal 등)는 shared 를 못 읽어 같은 식을 **미러**한다 — 함께 고칠 것.
 */
export function lostWhenText(lostDate?: string | null, lostTime?: string | null, departureTime?: string | null): string {
  const d = (lostDate ?? '').trim();
  const dep = (departureTime ?? '').trim();
  const slot = (lostTime ?? '').trim();
  const t = dep ? (/^\d{1,2}:\d{2}$/.test(dep) ? `출발 ${dep}` : `${dep} 출발`) : slot;
  return [d, t].filter(Boolean).join(' ');
}

/**
 * 콘솔 저장 직전 판정 — 입력칸 글 → `routes`·`routeSchedule` 패치.
 *   · 오류가 있으면 저장을 막고 «몇 행인지» 문장을 돌려준다(조용히 버리지 않는다).
 *   · `routes` = 시간표의 노선명 목록(문의 폼 드롭다운 정합).
 *   · 🔴 시간이 **하나도 없으면** `routeSchedule` 은 null(=필드 제거) — 노선명만 적힌 기존 거래처를
 *     열었다 저장했을 뿐인데 분실물 폼이 «목록 필수·기타 없음»으로 바뀌면 안 된다(새 게이트 기본값 = 현상 유지).
 *     시간을 한 칸이라도 적어야 시간표 모드가 켜진다.
 */
export function routeSchedulePatch(
  text: string,
):
  | { ok: false; message: string }
  | { ok: true; routes: string[]; routeSchedule: RouteScheduleEntry[] | null } {
  const { schedule, errors } = parseRouteSchedule(text);
  if (errors.length > 0) {
    const first = errors[0]!;
    const lines = Array.from(new Set(errors.map((e) => e.line)));
    const more = lines.length > 1 ? ` (잘못된 줄 ${lines.length}개: ${lines.join('·')}행)` : '';
    return { ok: false, message: `노선·출발시간표 ${first.line}행 — ${first.reason}${more}` };
  }
  const hasTimes = schedule.some((r) => r.times.length > 0);
  return { ok: true, routes: schedule.map((r) => r.route), routeSchedule: hasTimes ? schedule : null };
}
